import { useState, useMemo, useCallback, memo } from 'react';
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Checkbox,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Flex,
  Icon,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { Plus, Pencil } from 'lucide-react';
import { Milestone } from '../../types';
import { calculateMilestoneProgress } from '../rocks.util';
import { AddMilestoneForm } from './AddMilestoneForm';
import { EditMilestoneForm } from './EditMilestoneForm';

interface MilestonesSectionProps {
  milestones: Milestone[];
  rockId: string;
  addMilestone?: (rockId: string, task: string, dueDate: string, sortOrder: number) => Promise<void>;
  updateMilestone?: (milestoneId: string, task: string, dueDate: string) => Promise<void>;
  toggleMilestone?: (milestoneId: string, complete: boolean) => Promise<void>;
  onRefresh: () => Promise<void>;
}

const MilestonesSectionComponent = ({
  milestones,
  rockId,
  addMilestone,
  updateMilestone,
  toggleMilestone,
  onRefresh,
}: MilestonesSectionProps) => {
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [editingMilestoneId, setEditingMilestoneId] = useState<string | null>(null);
  const toast = useToast();

  const milestoneBg = useColorModeValue('blue.50', 'blue.900');
  const milestoneBorder = useColorModeValue('blue.200', 'blue.600');
  const milestoneText = useColorModeValue('blue.700', 'blue.300');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const textColorLight = useColorModeValue('gray.500', 'gray.400');
  const textColorLighter = useColorModeValue('gray.600', 'gray.500');

  const milestoneProgress = useMemo(() => calculateMilestoneProgress(milestones), [milestones]);

  const incompleteMilestones = useMemo(() => {
    return milestones
      .filter((m) => !m.complete)
      .sort((a, b) => {
        const dateA = new Date(a.dueDate).getTime();
        const dateB = new Date(b.dueDate).getTime();
        return dateA - dateB;
      });
  }, [milestones]);

  const completedMilestones = useMemo(() => {
    return milestones
      .filter((m) => m.complete)
      .sort((a, b) => {
        const dateA = new Date(a.dueDate).getTime();
        const dateB = new Date(b.dueDate).getTime();
        return dateA - dateB;
      });
  }, [milestones]);

  const formatDate = (date: Date | string) => {
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  const handleToggleMilestoneForm = useCallback(() => {
    setShowMilestoneForm((prev) => !prev);
  }, []);

  const handleMilestoneAdded = useCallback(async () => {
    await onRefresh();
    setShowMilestoneForm(false);
  }, [onRefresh]);

  const handleMilestoneUpdated = useCallback(async () => {
    await onRefresh();
    setEditingMilestoneId(null);
  }, [onRefresh]);

  const handleToggleMilestone = async (milestoneId: string, complete: boolean) => {
    if (!toggleMilestone) return;
    try {
      await toggleMilestone(milestoneId, complete);
      await onRefresh();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update milestone',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box bg={milestoneBg} border="1px" borderColor={milestoneBorder} borderRadius="lg" p={4}>
      <Flex justify="space-between" align="center" mb={2}>
        <HStack spacing={2}>
          <CheckCircleIcon color="blue.500" />
          <Text fontWeight="medium" fontSize="sm" color={milestoneText}>
            Milestones ({milestoneProgress}%)
          </Text>
        </HStack>
        {addMilestone && !showMilestoneForm && (
          <Button
            size="xs"
            colorScheme="blue"
            leftIcon={<Icon as={Plus} w={3} h={3} />}
            onClick={handleToggleMilestoneForm}
          >
            Add Milestone
          </Button>
        )}
      </Flex>

      {addMilestone && showMilestoneForm && (
        <AddMilestoneForm
          rockId={rockId}
          onAdd={(task: string, dueDate: string, sortOrder: number) =>
            addMilestone(rockId, task, dueDate, sortOrder)
          }
          onSuccess={handleMilestoneAdded}
          onCancel={() => setShowMilestoneForm(false)}
          existingCount={milestones.length}
        />
      )}

      {milestones.length > 0 && (
        <>
          <VStack align="stretch" spacing={2}>
            {incompleteMilestones.map((milestone) => (
              <Box key={milestone.id}>
                {editingMilestoneId === milestone.id ? (
                  <EditMilestoneForm
                    milestone={milestone}
                    onUpdate={updateMilestone!}
                    onSuccess={handleMilestoneUpdated}
                    onCancel={() => setEditingMilestoneId(null)}
                  />
                ) : (
                  <HStack spacing={2}>
                    <Checkbox
                      isChecked={milestone.complete}
                      onChange={(e) => handleToggleMilestone(milestone.id, e.target.checked)}
                      colorScheme="green"
                      isDisabled={!toggleMilestone}
                    />
                    {updateMilestone && (
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => setEditingMilestoneId(milestone.id)}
                        aria-label="Edit milestone"
                      >
                        <Icon as={Pencil} w={3} h={3} />
                      </Button>
                    )}
                    <Text
                      fontSize="sm"
                      textDecoration={milestone.complete ? 'line-through' : 'none'}
                      color={milestone.complete ? textColorLight : textColor}
                      flex={1}
                    >
                      {milestone.task}
                    </Text>
                    <Text fontSize="xs" color={textColorLight}>
                      {formatDate(milestone.dueDate)}
                    </Text>
                  </HStack>
                )}
              </Box>
            ))}
          </VStack>

          {completedMilestones.length > 0 && (
            <Accordion allowToggle size="sm" mt={2}>
              <AccordionItem border="none">
                <h2>
                  <AccordionButton _expanded={{ bg: 'blue.100' }} borderRadius="md" px={2} py={1}>
                    <Box flex="1" textAlign="left" fontSize="sm" fontWeight="medium" color={milestoneText}>
                      Completed ({completedMilestones.length})
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <VStack align="stretch" spacing={2} mt={2}>
                    {completedMilestones.map((milestone) => (
                      <HStack key={milestone.id} spacing={2} opacity={0.6}>
                        <Checkbox
                          isChecked={milestone.complete}
                          onChange={(e) => handleToggleMilestone(milestone.id, e.target.checked)}
                          colorScheme="green"
                          isDisabled={!toggleMilestone}
                        />
                        <Text
                          fontSize="sm"
                          textDecoration="line-through"
                          color={textColorLight}
                          flex={1}
                        >
                          {milestone.task}
                        </Text>
                        <Text fontSize="xs" color={textColorLight} ml="auto">
                          {formatDate(milestone.dueDate)}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          )}
        </>
      )}
    </Box>
  );
};

export const MilestonesSection = memo(MilestonesSectionComponent);
